import { useCallback, useEffect, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { borderGrey, primaryText, secondaryText, semiBoldText } from '../../../../styling/stylingHelper';
import { AdminLayout } from '../AdminLayout';
import {
  listScopes,
  rehydrate,
  hydratedSpecToLayout,
  applySideToLayout,
  effectiveSide,
  type ScopeClient,
} from '../../../../services/admin/cloudAgentService';
import type { CloudAgentScope, FilterBlock } from '../../../../services/admin/cloudAgentTypes';
import { scopesMatch } from '../../../../services/admin/cloudAgentScope';
import { useAppCloudAgentStore } from '../../../../stores/appCloudAgentStore';
import { ChatInput } from './ChatInput';
import { ChatTranscript } from './ChatTranscript';
import { CostWidget } from './CostWidget';
import { ScopePicker } from './ScopePicker';

/**
 * Super-admin Cloud Agent (v2): a conversation about one restaurant, or about every restaurant
 * across every client. The transcript, conversation id and cost accounting live in
 * `useAppCloudAgentStore`; this page owns only the scope source and the composer's draft text.
 */
const CloudAgentPage = () => {
  const { t } = useTranslation();

  const [clients, setClients] = useState<ScopeClient[]>([]);
  const [allRestaurantsCount, setAllRestaurantsCount] = useState(0);
  const [scopesError, setScopesError] = useState(false);
  const [question, setQuestion] = useState('');
  const [filteringTurnId, setFilteringTurnId] = useState<string | null>(null);
  /** Why the last filter re-hydration failed, and on which turn to show it. */
  const [filterError, setFilterError] = useState<{ turnId: string; message: string } | null>(null);

  const scope = useAppCloudAgentStore((s) => s.scope);
  const scopeLabel = useAppCloudAgentStore((s) => s.scopeLabel);
  const turns = useAppCloudAgentStore((s) => s.turns);
  const phase = useAppCloudAgentStore((s) => s.phase);
  const usageTurns = useAppCloudAgentStore((s) => s.usageTurns);
  const usageConversation = useAppCloudAgentStore((s) => s.usageConversation);
  const setScope = useAppCloudAgentStore((s) => s.setScope);
  const send = useAppCloudAgentStore((s) => s.send);
  const replaceSpec = useAppCloudAgentStore((s) => s.replaceSpec);
  const newConversation = useAppCloudAgentStore((s) => s.newConversation);
  const cancel = useAppCloudAgentStore((s) => s.cancel);

  const isStreaming = phase === 'streaming';

  // Drop any in-flight stream when leaving the page (the transcript survives in the store).
  useEffect(() => cancel, [cancel]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listScopes();
        if (cancelled) return;
        setClients(res.clients);
        // The server may send the synthetic all-restaurants count; otherwise count what we have.
        setAllRestaurantsCount(
          res.allRestaurantsCount ?? res.clients.reduce((sum, c) => sum + c.restaurants.length, 0)
        );
        // Default to the first restaurant so the page is askable immediately.
        const firstClient = res.clients.find((c) => c.restaurants.length > 0);
        const firstRestaurant = firstClient?.restaurants[0];
        if (firstClient && firstRestaurant && !useAppCloudAgentStore.getState().scope) {
          setScope(
            { mode: 'restaurant', restaurantId: firstRestaurant.restaurantId, userId: firstClient.clientId },
            firstRestaurant.restaurantName
          );
        }
      } catch {
        if (!cancelled) setScopesError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setScope]);

  /** Scope is fixed per conversation — switching it always starts a fresh one. */
  const handleScopeChange = useCallback(
    (next: CloudAgentScope, label: string) => {
      // Set comparison, not array/reference: re-applying the same restaurants in a different
      // order must not cost the admin their conversation.
      if (scopesMatch(scope, next)) return;
      if (turns.length > 0 && !window.confirm(t('cloudAgent.scope.switchConfirm'))) return;
      if (turns.length > 0) newConversation();
      setScope(next, label);
    },
    [scope, turns.length, newConversation, setScope, t]
  );

  const handleNewConversation = useCallback(() => {
    if (turns.length > 0 && !window.confirm(t('cloudAgent.newConversationConfirm'))) return;
    newConversation();
    setQuestion('');
  }, [turns.length, newConversation, t]);

  /** A `filter` block changed: rebuild that turn's data-free layout and re-hydrate (NO Claude call). */
  const handleFilterChange = useCallback(
    async (turnId: string, changed: FilterBlock, selected: string[]) => {
      const turn = useAppCloudAgentStore.getState().turns.find((entry) => entry.id === turnId);
      const currentScope = useAppCloudAgentStore.getState().scope;
      if (!turn?.spec || !currentScope) return;
      const options = changed.options && changed.options.length > 0 ? changed.options : ['food', 'drinks'];
      const side = effectiveSide(selected, options);

      let layout = hydratedSpecToLayout(turn.spec);
      layout = {
        ...layout,
        blocks: layout.blocks.map((b) =>
          b.type === 'filter' && b.text === changed.text ? { ...b, selected } : b
        ),
      };
      layout = applySideToLayout(layout, side);

      setFilteringTurnId(turnId);
      setFilterError(null);
      try {
        const { spec: newSpec } = await rehydrate({ scope: currentScope, layout });
        replaceSpec(turnId, newSpec);
      } catch (err) {
        // Keep the previous spec, but say so — a silent no-op on a filter click reads as a
        // frozen UI. `Invalid layout: no usable blocks` (every block dropped for the picked
        // side) is the expected 400 here, so show the server's own reason.
        console.error('[CloudAgent] Filter rehydrate failed:', err);
        setFilterError({
          turnId,
          message: err instanceof Error ? err.message : t('cloudAgent.error'),
        });
      } finally {
        setFilteringTurnId(null);
      }
    },
    [replaceSpec, t]
  );

  const canAsk = !isStreaming && !!scope;
  const canSend = canAsk && question.trim().length > 0;

  const handleSend = () => {
    if (!canSend) return;
    const q = question;
    setQuestion('');
    void send(q);
  };

  const handleChip = (chip: string) => {
    if (!canAsk) return;
    setQuestion('');
    void send(chip);
  };

  const isEmpty = turns.length === 0;
  const hasConversation = turns.length > 0 || usageTurns.length > 0;

  return (
    <AdminLayout>
      <div className="flex flex-col max-w-4xl mx-auto min-h-[calc(100vh-12rem)]">
        {/* Scope + cost + new conversation. Wraps to two rows on narrow screens. */}
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
          {hasConversation && (
            <button
              type="button"
              onClick={handleNewConversation}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition',
                borderGrey,
                'hover:bg-purple-50 dark:hover:bg-purple-900/20',
                secondaryText
              )}
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              {t('cloudAgent.newConversation')}
            </button>
          )}
          <CostWidget turns={usageTurns} conversation={usageConversation} isStreaming={isStreaming} />
          <ScopePicker
            clients={clients}
            allRestaurantsCount={allRestaurantsCount}
            scopesError={scopesError}
            scope={scope}
            scopeLabel={scopeLabel}
            onScopeChange={handleScopeChange}
          />
        </div>

        {/* Hero — only before the first question. */}
        {isEmpty && (
          <div className="text-center my-auto py-10">
            <h1 className={cn(primaryText, 'text-2xl', semiBoldText)}>{t('cloudAgent.title')}</h1>
            <p className={cn(secondaryText, 'text-sm mt-1')}>{t('cloudAgent.subtitle')}</p>
          </div>
        )}

        {!isEmpty && (
          <div className="flex-1">
            <ChatTranscript
              turns={turns}
              onFilterChange={handleFilterChange}
              filteringTurnId={filteringTurnId}
              filterError={filterError}
            />
          </div>
        )}

        <ChatInput
          value={question}
          onChange={setQuestion}
          onSend={handleSend}
          onChipClick={handleChip}
          canSend={canSend}
          canAsk={canAsk}
          isStreaming={isStreaming}
          showSuggestions={isEmpty}
        />
      </div>
    </AdminLayout>
  );
};

export default CloudAgentPage;
